import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { Channel } from 'icreate/common/models/channel';
import {
    ChannelType,
    getAllTypesSet,
    isDigital,
    isDigitalContact,
    isDigitalMotor,
} from 'icreate/common/models/channel-type';
import { getMotorChannelStrings } from 'icreate/common/models/motor-alarm-setting';
import { OutputSettingChannel, STATUS_KEY_BIT_VALUES, TriggerCondition } from 'icreate/common/models/output-settings';
import { AlarmPropertyList } from 'icreate/common/models/repose-group';
import { lastValueFrom, take } from 'rxjs';

interface SelectorOption {
    value: any;
    key: string;
}

@Component({
    selector: 'app-source-channel-selector',
    templateUrl: './source-channel-selector.component.html',
    styleUrls: ['./source-channel-selector.component.css', './../digital-output-setting.component.css'],
    standalone: false,
})
export class SourceChannelSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly alarmSettings: AlarmPropertyList[] = [
        AlarmPropertyList.alarmSettingVeryHigh,
        AlarmPropertyList.alarmSettingHigh,
        AlarmPropertyList.alarmSettingLow,
        AlarmPropertyList.alarmSettingVeryLow,
        AlarmPropertyList.alarmSettingFeedbackError,
        AlarmPropertyList.alarmSettingSensorFail,
    ];
    readonly triggerConditions: SelectorOption[] = [
        {
            value: TriggerCondition.ALARM,
            key: 'output-settings.DO.dialog.trigger-condition.alarm',
        },
        {
            value: TriggerCondition.STATUS,
            key: 'output-settings.DO.dialog.trigger-condition.status',
        },
    ];
    readonly alarmTrigger = TriggerCondition.ALARM;
    readonly statusTrigger = TriggerCondition.STATUS;
    readonly triggerSelector = 'trigger-condition-selector';
    readonly optionsSelector = 'options-selector';

    @ViewChild('triggerSelElement') triggerSelElement: ElementRef;
    @ViewChild('optionsSelElement') optionsSelElement: ElementRef;

    selectedChannel: Channel;
    allowedTypesSet: Set<ChannelType> = new Set(getAllTypesSet());
    isTriggerConditionOpen: boolean;
    isOptionsOpen: boolean;
    triggerCondition: TriggerCondition;
    alarmConditionsSet: Set<AlarmPropertyList> = new Set();
    statusConditionsSet: Set<string> = new Set();
    optionsTop: string;
    optionsLeft: string;
    optionsWidth: string;
    hasTriggerError: boolean = false;
    hasOptionError: boolean = false;

    @HostListener('document:click', [])
    clickOut() {
        if (this.isOptionsOpen) {
            this.isOptionsOpen = false;
            this.hasOptionError =
                (this.triggerCondition == TriggerCondition.ALARM && this.alarmConditionsSet.size == 0) ||
                (this.triggerCondition == TriggerCondition.STATUS && this.statusConditionsSet.size == 0);
        }
        if (this.isTriggerConditionOpen) {
            this.isTriggerConditionOpen = false;
            this.hasTriggerError = !this.triggerCondition || this.triggerCondition == TriggerCondition.UNDEFINED;
        }
    }

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vesselId: string;
            settingChannel: OutputSettingChannel;
        },
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<SourceChannelSelectorComponent>,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        if (this.data.settingChannel) {
            this.triggerCondition = this.data.settingChannel.triggerCondition;
            this.selectedChannel = this.data.settingChannel.channel;
            const alarmConditions = this.data.settingChannel.alarmCondition ?? [];
            this.alarmConditionsSet = new Set(alarmConditions);
            const statusConditions = this.data.settingChannel.statusCondition ?? [];
            this.statusConditionsSet = new Set(statusConditions);
        }
    }

    ngAfterViewInit(): void {
        if (!this.selectedChannel) {
            this.selectChannel();
        }
    }

    ngOnDestroy(): void {}

    close() {
        this.dialogRef.close();
    }

    async selectChannel() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.data.vesselId,
                assignedChannel: this.selectedChannel,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: this.allowedTypesSet,
                has2Steps: true,
                multipleSelection: false,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());

        if (channelSelected) {
            this.isOptionsOpen = false;
            this.isTriggerConditionOpen = false;
            this.triggerCondition = undefined;
            this.selectedChannel = channelSelected;
        } else this.dialogRef.close();

        this.cdr.detectChanges();
    }

    isDigitalChannel(): boolean {
        if (!this.selectedChannel) return false;
        return isDigital(this.selectedChannel.fcuData.channelType);
    }

    getMeasurableRangeStr() {
        return (
            this.selectedChannel.fcuData.measurableRange.min +
            ' ' +
            this.selectedChannel.opsData.measurementUnit +
            ' - ' +
            this.selectedChannel.fcuData.measurableRange.max +
            ' ' +
            this.selectedChannel.opsData.measurementUnit
        );
    }

    getNormalRangeStr() {
        return (
            this.selectedChannel.fcuData.normalRange.min +
            ' ' +
            this.selectedChannel.opsData.measurementUnit +
            ' - ' +
            this.selectedChannel.fcuData.normalRange.max +
            ' ' +
            this.selectedChannel.opsData.measurementUnit
        );
    }

    getAlarmRangeStr(alarmProperty: AlarmPropertyList) {
        return (
            this.selectedChannel.fcuData[alarmProperty].setPoint + ' ' + this.selectedChannel.opsData.measurementUnit
        );
    }

    openTriggerCondition(event) {
        this.isOptionsOpen = false;
        if (this.isTriggerConditionOpen) {
            this.updatePosition();
            this.isTriggerConditionOpen = false;
            this.hasTriggerError = !this.triggerCondition || this.triggerCondition == TriggerCondition.UNDEFINED;
        } else {
            this.hasTriggerError = false;
            this.cdr.detectChanges();
            this.updatePosition(this.triggerSelElement);
            this.isTriggerConditionOpen = true;
        }
        event.stopPropagation();
    }

    openOptions(event) {
        this.isTriggerConditionOpen = false;
        if (this.isOptionsOpen) {
            this.updatePosition();
            this.isOptionsOpen = false;
            this.hasOptionError =
                (this.triggerCondition == TriggerCondition.ALARM && this.alarmConditionsSet.size == 0) ||
                (this.triggerCondition == TriggerCondition.STATUS && this.statusConditionsSet.size == 0);
        } else {
            this.hasOptionError = false;
            this.cdr.detectChanges();
            this.updatePosition(this.optionsSelElement);
            this.isOptionsOpen = true;
        }
        event.stopPropagation();
    }

    getOptionsLegend() {
        if (!this.triggerCondition) return;
        else if (this.triggerCondition == TriggerCondition.ALARM) {
            return 'output-settings.DO.dialog.trigger-condition.alarm';
        } else if (this.triggerCondition == TriggerCondition.STATUS) {
            return 'output-settings.DO.dialog.trigger-condition.status';
        }
    }

    geDisplayValue() {
        const condition = this.triggerConditions.find(condition => condition.value == this.triggerCondition);
        return condition;
    }

    triggerConditionSelected(option: SelectorOption) {
        if (!this.isDigitalChannel() && option.value == TriggerCondition.STATUS) return;
        this.triggerCondition = option.value;
        this.isTriggerConditionOpen = false;
    }

    isOptionDisabled(option: TriggerCondition): boolean {
        if (!this.selectedChannel) return false;
        if (!this.isDigitalChannel() && option == TriggerCondition.STATUS) return true;
        return false;
    }

    getOptions() {
        if (!this.triggerCondition || !this.selectedChannel) return [];
        if (this.triggerCondition == TriggerCondition.ALARM) {
            const enabledSettings = this.alarmSettings.filter(setting => this.selectedChannel.fcuData[setting].enable);
            return enabledSettings;
        } else if (this.triggerCondition == TriggerCondition.STATUS) {
            if (!this.isDigitalChannel()) return;
            if (isDigitalContact(this.selectedChannel.fcuData.channelType)) {
                return [
                    this.selectedChannel.fcuData.digitalAlarmSetting.statusStringContactOn,
                    this.selectedChannel.fcuData.digitalAlarmSetting.statusStringContactOff,
                ];
            } else if (isDigitalMotor(this.selectedChannel.fcuData.channelType)) {
                return getMotorChannelStrings(this.selectedChannel.fcuData.motorChannelSetting);
            }
        }
    }

    multipleOptionSelected(option) {
        if (!this.triggerCondition) return;
        if (this.triggerCondition == TriggerCondition.ALARM) {
            if (this.alarmConditionsSet.has(option)) this.alarmConditionsSet.delete(option);
            else this.alarmConditionsSet.add(option);
        } else if (this.triggerCondition == TriggerCondition.STATUS) {
            if (!this.isDigitalChannel()) return;
            if (isDigitalContact(this.selectedChannel.fcuData.channelType)) {
                if (this.statusConditionsSet.has(option)) this.statusConditionsSet.delete(option);
                else {
                    this.statusConditionsSet.clear();
                    this.statusConditionsSet.add(option);
                }
            } else if (isDigitalMotor(this.selectedChannel.fcuData.channelType)) {
                if (this.statusConditionsSet.has(option)) this.statusConditionsSet.delete(option);
                else this.statusConditionsSet.add(option);
            }
        }
    }

    getCheckboxText(setting: AlarmPropertyList | string) {
        if (!this.triggerCondition || !this.selectedChannel) return;
        if (this.triggerCondition == TriggerCondition.ALARM) {
            if (!this.isDigitalChannel()) return this.selectedChannel.fcuData[setting].statusString;
            else if (isDigitalContact(this.selectedChannel.fcuData.channelType)) {
                if (this.selectedChannel.fcuData.digitalAlarmSetting.normalOpenFlag)
                    return this.selectedChannel.fcuData.digitalAlarmSetting.statusStringContactOff;
                else return this.selectedChannel.fcuData.digitalAlarmSetting.statusStringContactOn;
            } else if (isDigitalMotor(this.selectedChannel.fcuData.channelType)) {
                if (setting == AlarmPropertyList.alarmSettingFeedbackError)
                    return this.selectedChannel.fcuData[setting].statusString;
                else if (setting == AlarmPropertyList.alarmSettingMotor) return 'channel.label.alarm.motor-setting';
            }
        } else if (this.triggerCondition == TriggerCondition.STATUS) {
            if (!this.isDigitalChannel()) return;
            return setting;
        }
    }

    getMultpleDisplayValue() {
        if (!this.triggerCondition) return;
        if (this.triggerCondition == TriggerCondition.ALARM) {
            const selectedSettings = this.alarmSettings.filter(setting => this.alarmConditionsSet.has(setting));
            const statusStrings = selectedSettings.map(setting => this.selectedChannel.fcuData[setting].statusString);
            return statusStrings.join(', ');
        } else if (this.triggerCondition == TriggerCondition.STATUS) {
            const statusStrings = [...this.statusConditionsSet];
            return statusStrings.join(', ');
        }
    }

    isOptionSelected(option) {
        if (this.triggerCondition == TriggerCondition.ALARM) {
            return this.alarmConditionsSet.has(option);
        } else if (this.triggerCondition == TriggerCondition.STATUS) {
            return this.statusConditionsSet.has(option);
        }
        return false;
    }

    private updatePosition(elementRef?) {
        if (!elementRef) {
            this.optionsTop = null;
            this.optionsLeft = null;
            this.optionsWidth = null;
            return;
        }
        const selectorPosition = elementRef.nativeElement.getBoundingClientRect();

        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        let top = selectorPosition.bottom + scrollTop;

        this.optionsTop = top + 1 + 'px';
        this.optionsLeft = selectorPosition.left + scrollLeft + 'px';
        this.optionsWidth = selectorPosition.width + 'px';
    }

    isSaveDisabled(): boolean {
        if (!this.selectedChannel) return true;
        if (!this.triggerCondition) return true;
        if (this.triggerCondition == TriggerCondition.ALARM && this.alarmConditionsSet.size == 0) return true;
        else if (this.triggerCondition == TriggerCondition.STATUS && this.statusConditionsSet.size == 0) return true;
        return false;
    }

    getMotorChannelValue(): number {
        let value = 0;
        const motorSetting = this.selectedChannel.fcuData.motorChannelSetting;

        for (const [key, bitValue] of Object.entries(STATUS_KEY_BIT_VALUES)) {
            const statusString = motorSetting[key];
            if (this.statusConditionsSet.has(statusString)) {
                value += bitValue;
            }
        }

        return value;
    }

    getDigitalStatusValue() {
        if (!isDigital(this.selectedChannel.fcuData.channelType)) return;
        let statusValue;
        if (isDigitalContact(this.selectedChannel.fcuData.channelType)) {
            const contactOnString = this.selectedChannel.fcuData.digitalAlarmSetting.statusStringContactOn;
            const contactOffString = this.selectedChannel.fcuData.digitalAlarmSetting.statusStringContactOff;
            const activeStatus = [...this.statusConditionsSet][0];
            if (!activeStatus) return;
            //!Need to confirm if this is correct
            if (activeStatus == contactOnString) statusValue = 1;
            else if (activeStatus == contactOffString) statusValue = 0;
        } else if (isDigitalMotor(this.selectedChannel.fcuData.channelType)) {
            statusValue = this.getMotorChannelValue();
        }

        return statusValue;
    }

    save() {
        if (this.isSaveDisabled()) return;

        this.dialogRef.close({
            selectedChannel: this.selectedChannel,
            triggerCondition: this.triggerCondition,
            alarmConditionsSet: this.alarmConditionsSet,
            statusConditionsSet: this.statusConditionsSet,
            statusValue: this.getDigitalStatusValue(),
        });
    }
}
